import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ComparisonMatrix = ({ services }) => {
  const [selectedService, setSelectedService] = useState(null);

  const capabilities = [
    { id: 'strategy', label: 'Strategy & Planning', icon: 'Target' },
    { id: 'development', label: 'Development', icon: 'Code' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
    { id: 'security', label: 'Security', icon: 'Shield' },
    { id: 'support', label: 'Ongoing Support', icon: 'Headphones' }
  ];

  const getCapabilityLevel = (serviceId, capabilityId) => {
    const levels = {
      'software-development': {
        strategy: 'high',
        development: 'high',
        analytics: 'medium',
        security: 'high',
        support: 'high'
      },
      'ai-models': {
        strategy: 'high',
        development: 'high',
        analytics: 'high',
        security: 'medium',
        support: 'medium'
      },
      'analytics': {
        strategy: 'medium',
        development: 'medium',
        analytics: 'high',
        security: 'medium',
        support: 'high'
      },
      'cybersecurity': {
        strategy: 'high',
        development: 'medium',
        analytics: 'medium',
        security: 'high',
        support: 'high'
      },
      'ai-engineering': {
        strategy: 'high',
        development: 'high',
        analytics: 'high',
        security: 'high',
        support: 'medium'
      }
    };

    return levels[serviceId]?.[capabilityId] || 'low';
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'high': return 'bg-success text-white';
      case 'medium': return 'bg-warning text-white';
      case 'low': return 'bg-muted text-muted-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'high': return 'CheckCircle2';
      case 'medium': return 'AlertCircle';
      case 'low': return 'Circle';
      default: return 'Circle';
    }
  };

  return (
    <div className="bg-white rounded-xl border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="Grid3X3" size={20} className="text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Service Capabilities Matrix</h3>
          <p className="text-sm text-muted-foreground">Compare our expertise levels across different domains</p>
        </div>
      </div>

      {/* Desktop Matrix View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">
                Service / Capability
              </th>
              {capabilities.map((capability) => (
                <th key={capability.id} className="text-center py-3 px-2 min-w-[120px]">
                  <div className="flex flex-col items-center space-y-1">
                    <Icon name={capability.icon} size={16} className="text-primary" />
                    <span className="text-xs font-medium text-foreground">{capability.label}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {services.map((service) => (
              <tr 
                key={service.id}
                className={`border-b border-border hover:bg-muted/30 transition-colors cursor-pointer ${
                  selectedService === service.id ? 'bg-primary/5' : ''
                }`}
                onClick={() => setSelectedService(selectedService === service.id ? null : service.id)}
              >
                <td className="py-4 px-2">
                  <div className="flex items-center space-x-3">
                    <Icon name={service.icon} size={18} className="text-primary" />
                    <span className="font-medium text-foreground">{service.title}</span>
                  </div>
                </td>
                {capabilities.map((capability) => {
                  const level = getCapabilityLevel(service.id, capability.id);
                  return (
                    <td key={capability.id} className="py-4 px-2 text-center">
                      <div className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${getLevelColor(level)}`}>
                        <Icon name={getLevelIcon(level)} size={16} />
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile/Tablet Accordion View */}
      <div className="lg:hidden space-y-3">
        {services.map((service) => (
          <div key={service.id} className="border border-border rounded-lg">
            <button
              onClick={() => setSelectedService(selectedService === service.id ? null : service.id)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Icon name={service.icon} size={18} className="text-primary" />
                <span className="font-medium text-foreground">{service.title}</span>
              </div>
              <Icon 
                name={selectedService === service.id ? 'ChevronUp' : 'ChevronDown'} 
                size={16} 
                className="text-muted-foreground" 
              />
            </button>
            
            {selectedService === service.id && (
              <div className="px-4 pb-4 border-t border-border">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                  {capabilities.map((capability) => {
                    const level = getCapabilityLevel(service.id, capability.id);
                    return (
                      <div key={capability.id} className="flex items-center space-x-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${getLevelColor(level)}`}>
                          <Icon name={getLevelIcon(level)} size={12} />
                        </div>
                        <div>
                          <div className="text-xs font-medium text-foreground">{capability.label}</div>
                          <div className="text-xs text-muted-foreground capitalize">{level}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-center space-x-6 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full bg-success"></div>
            <span className="text-muted-foreground">High Expertise</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full bg-warning"></div>
            <span className="text-muted-foreground">Medium Expertise</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full bg-muted"></div>
            <span className="text-muted-foreground">Basic Coverage</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonMatrix;